/**
 * Graph Explorer - Utils Module
 * Shared utility functions
 */

(function(global) {
    'use strict';

    // ============================================
    // Color Palette
    // ============================================
    const COLOR_PALETTE = [
        '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
        '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe',
        '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000',
        '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080'
    ];

    function getColorPalette() {
        return [...COLOR_PALETTE];
    }

    function getColorForIndex(index) {
        return COLOR_PALETTE[index % COLOR_PALETTE.length];
    }

    // ============================================
    // Debounce / Throttle
    // ============================================
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    function throttle(func, limit) {
        let inThrottle;
        return function(...args) {
            if (!inThrottle) {
                func.apply(this, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }

    // ============================================
    // DOM Helpers
    // ============================================
    function createElement(tag, attributes = {}, children = []) {
        const el = document.createElement(tag);
        
        Object.entries(attributes).forEach(([key, value]) => {
            if (key === 'className') {
                el.className = value;
            } else if (key === 'style' && typeof value === 'object') {
                Object.assign(el.style, value);
            } else if (key.startsWith('on') && typeof value === 'function') {
                el.addEventListener(key.slice(2).toLowerCase(), value);
            } else if (key === 'dataset' && typeof value === 'object') {
                Object.entries(value).forEach(([dataKey, dataValue]) => {
                    el.dataset[dataKey] = dataValue;
                });
            } else {
                el.setAttribute(key, value);
            }
        });
        
        children.forEach(child => {
            if (typeof child === 'string') {
                el.appendChild(document.createTextNode(child));
            } else if (child instanceof Node) {
                el.appendChild(child);
            }
        });
        
        return el;
    }

    function $(selector, context = document) {
        return context.querySelector(selector);
    }

    function $$(selector, context = document) {
        return Array.from(context.querySelectorAll(selector));
    }

    // ============================================
    // Data Helpers
    // ============================================
    function sanitizeForPostMessage(obj) {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj !== 'object') return obj;
        
        if (Array.isArray(obj)) {
            return obj.map(sanitizeForPostMessage);
        }
        
        const result = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const value = obj[key];
                if (typeof value === 'function') continue;
                if (value instanceof Node) continue;
                if (typeof value === 'object' && value !== null) {
                    if (value.constructor && value.constructor.name !== 'Object' && value.constructor.name !== 'Array') {
                        continue;
                    }
                    result[key] = sanitizeForPostMessage(value);
                } else {
                    result[key] = value;
                }
            }
        }
        return result;
    }

    function deepClone(obj) {
        return JSON.parse(JSON.stringify(obj));
    }

    // ============================================
    // Format Helpers
    // ============================================
    function formatNumber(num, decimals = 2) {
        if (typeof num !== 'number') return '-';
        if (Number.isInteger(num)) return num.toString();
        return num.toFixed(decimals);
    }

    function truncateText(text, maxLength = 50) {
        if (!text || text.length <= maxLength) return text;
        return text.slice(0, maxLength - 3) + '...';
    }

    // ============================================
    // Accessibility Helpers
    // ============================================
    function createAnnouncer() {
        let announcer = document.getElementById('explorer-announcer');
        if (!announcer) {
            announcer = document.createElement('div');
            announcer.id = 'explorer-announcer';
            announcer.className = 'sr-only';
            announcer.setAttribute('role', 'status');
            announcer.setAttribute('aria-live', 'polite');
            announcer.setAttribute('aria-atomic', 'true');
            document.body.appendChild(announcer);
        }
        return announcer;
    }

    function announce(message) {
        const announcer = document.getElementById('sr-announcer') || createAnnouncer();
        if (announcer) {
            announcer.textContent = message;
            // Clear after a short delay so repeated messages are announced
            setTimeout(() => {
                announcer.textContent = '';
            }, 1000);
        }
    }

    /**
     * Safe wrapper for GraphExplorer.announceChange
     * This function can be called even before GraphExplorer is initialized.
     * Falls back to ExplorerUtils.announce if GraphExplorer is not available.
     */
    function announceChange(message) {
        // Try GraphExplorer.announceChange first (the full implementation)
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.announceChange) {
            GraphExplorer.announceChange(message);
            return;
        }
        // Fall back to simple announce
        announce(message);
        console.log(message);
    }

    // ============================================
    // Storage Helpers
    // ============================================
    function saveToStorage(key, value) {
        try {
            localStorage.setItem(`explorer_${key}`, JSON.stringify(value));
            return true;
        } catch (e) {
            console.warn('Failed to save to storage:', e);
            return false;
        }
    }

    function loadFromStorage(key, defaultValue = null) {
        try {
            const stored = localStorage.getItem(`explorer_${key}`);
            return stored ? JSON.parse(stored) : defaultValue;
        } catch (e) {
            console.warn('Failed to load from storage:', e);
            return defaultValue;
        }
    }

    // ============================================
    // Export Helpers
    // ============================================
    function downloadFile(content, filename, mimeType = 'application/json') {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    function canvasToBlob(canvas) {
        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Failed to create blob'));
                }
            }, 'image/png');
        });
    }

    // ============================================
    // Public API
    // ============================================
    global.ExplorerUtils = {
        // Colors
        getColorPalette,
        getColorForIndex,
        
        // Timing
        debounce,
        throttle,
        
        // DOM
        createElement,
        $,
        $$,
        
        // Data
        sanitizeForPostMessage,
        deepClone,
        
        // Format
        formatNumber,
        truncateText,
        
        // Accessibility
        createAnnouncer,
        announce,
        announceChange,  // Safe wrapper for GraphExplorer.announceChange
        
        // Storage
        saveToStorage,
        loadFromStorage,
        
        // Export
        downloadFile,
        canvasToBlob
    };

})(window);
